import moment from "moment";
import { formatNumberWithCommas } from "../../../config/utils";
import { isSuperAdminRole } from "../../../constants/roleConstants";

export const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const PNL_STATUS = { FINAL: 1, PROVISIONAL: 2, IN_PROGRESS: 3, AWAITING_RETURNS: 4 };
export const SHIPPING_LABELS = "ShippingLabels";
export const DETAIL_PAGE_SIZE = 100;

const KNOWN_CATEGORIES = new Set(["Tasks", "CarrierConnect", "Invoices", SHIPPING_LABELS]);
const YEAR_OPTION_COUNT = 3;
const DEFAULT_ERROR_MESSAGE = "Unable to load the Profit & Loss report.";

const toAmount = (value) => Number(value) || 0;
const round2 = (value) => Math.round(value * 100) / 100;
const padMonth = (month) => String(month).padStart(2, "0");
const monthKey = (row) => `${row.year}-${padMonth(row.month)}`;

export const monthLabel = (year, month) => `${MONTH_NAMES[month - 1]} ${year}`;

// The report is Super Admin only; the backend answers 403 for everyone else.
export const isPnlReportUser = (user) => Boolean(user?.isSuperAdmin) || isSuperAdminRole(user);

// "CarrierConnect" -> "Carrier connect"; new backend categories get a readable label automatically.
export const getCategoryLabel = (category) => {
  const spaced = String(category ?? "").replaceAll(/([a-z])([A-Z])/g, "$1 $2").trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase() : "";
};

export const formatMoney = (value) => {
  const amount = round2(toAmount(value));
  return `${amount < 0 ? "-" : ""}$${formatNumberWithCommas(Math.abs(amount))}`;
};

export const formatLockDate = (value) => {
  const parsed = moment.utc(value);
  return value && parsed.isValid() ? parsed.format("DD MMM YYYY") : "";
};

export const getErrorMessage = (body) =>
  body?.Message?.AppStatusDescription || body?.message || DEFAULT_ERROR_MESSAGE;

// Never rejects: resolves to { rows, total, error } so callers only branch on the error text.
export const loadEntityList = async (request) => {
  try {
    const body = (await request())?.data;
    if (body?.Status === 200) {
      const rows = body.EntityList || [];
      return { rows, total: Number(body.TotalRecords) || rows.length, error: "" };
    }
    return { rows: [], total: 0, error: getErrorMessage(body) };
  } catch (error) {
    return { rows: [], total: 0, error: getErrorMessage(error?.response?.data) };
  }
};

export const toggleKey = (keys, key) => {
  const next = new Set(keys);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
};

export const getStatusInfo = (row) => {
  switch (row?.statusCode) {
    case PNL_STATUS.FINAL:
      return { kind: "final", label: "Final" };
    case PNL_STATUS.PROVISIONAL: {
      const lockDate = formatLockDate(row.lockDate);
      return { kind: "prov", label: lockDate ? `Provisional, locks ${lockDate}` : "Provisional" };
    }
    case PNL_STATUS.IN_PROGRESS:
      return { kind: "live", label: "In progress" };
    case PNL_STATUS.AWAITING_RETURNS:
      return { kind: "prov", label: "Provisional, awaiting returns" };
    default:
      return { kind: "live", label: row?.status || "In progress" };
  }
};

export const getMonthStatus = (rows) => {
  const kinds = new Set(rows.map((row) => getStatusInfo(row).kind));
  if (kinds.has("live")) return { kind: "live", label: "In progress" };
  if (kinds.has("prov")) return { kind: "prov", label: "Partially final" };
  return { kind: "final", label: "Final" };
};

export const getLockText = (row) => {
  const lockDate = formatLockDate(row?.lockDate);
  switch (row?.statusCode) {
    case PNL_STATUS.FINAL:
      return lockDate
        ? `Locked on ${lockDate}. Refresh will not change these values.`
        : "Locked. Refresh will not change these values.";
    case PNL_STATUS.PROVISIONAL:
      return lockDate
        ? `Open for corrections until ${lockDate}. Refresh recalculates this row.`
        : "Open for corrections. Refresh recalculates this row.";
    case PNL_STATUS.AWAITING_RETURNS:
      return "Waiting for carrier refunds and adjustments. Values update until this month's shipping-label returns are closed.";
    default:
      return lockDate
        ? `Current month. Values update on each refresh. Locks on ${lockDate}.`
        : "Current month. Values update on each refresh until month end.";
  }
};

export const netClass = (value) => (toAmount(value) < 0 ? "neg" : "");

// Net as a share of gross; blank when there is no positive gross to compare against.
export const formatNetMargin = (net, gross) => {
  const grossAmount = toAmount(gross);
  if (grossAmount <= 0) return "";
  return `${((toAmount(net) / grossAmount) * 100).toFixed(1)}%`;
};

// Months and the current month are evaluated in UTC because the backend buckets by UTC.
export const getYearOptions = (now = new Date()) =>
  Array.from({ length: YEAR_OPTION_COUNT }, (_, index) => now.getUTCFullYear() - index);

export const getMonthOptions = (year, now = new Date()) => {
  const lastMonth = Number(year) === now.getUTCFullYear() ? now.getUTCMonth() + 1 : 12;
  return Array.from({ length: lastMonth }, (_, index) => ({
    value: index + 1,
    label: monthLabel(year, index + 1),
  }));
};

// Opens on the previous month; in January that is December of the previous year.
export const getDefaultFilters = (now = new Date()) => {
  const currentMonth = now.getUTCMonth() + 1;
  const month = currentMonth === 1 ? 12 : currentMonth - 1;
  return {
    year: currentMonth === 1 ? now.getUTCFullYear() - 1 : now.getUTCFullYear(),
    fromMonth: month,
    toMonth: month,
    categories: [],
    subTypes: [],
    companyIds: [],
    sellerIds: [],
    warehouseIds: [],
  };
};

// Empty lists mean "all"; the API treats them the same way.
export const buildEntityFilter = (filters) => ({
  companyIdList: filters.companyIds ?? [],
  sellerIdList: filters.sellerIds ?? [],
  warehouseIdList: filters.warehouseIds ?? [],
});

const keepAvailable = (options, selected = []) => selected.filter((id) => options.some((option) => option.value === id));

// Drops selected sellers/warehouses that are no longer offered; returns the same object when nothing changed.
export const pruneEntitySelections = (filters, sellerOptions, warehouseOptions) => {
  const sellerIds = keepAvailable(sellerOptions, filters.sellerIds);
  const warehouseIds = keepAvailable(warehouseOptions, filters.warehouseIds);
  const unchanged =
    sellerIds.length === (filters.sellerIds ?? []).length && warehouseIds.length === (filters.warehouseIds ?? []).length;
  return unchanged ? filters : { ...filters, sellerIds, warehouseIds };
};

export const applyFilterChange = (filters, field, rawValue, now = new Date()) => {
  const isNumeric = ["year", "fromMonth", "toMonth"].includes(field);
  const next = { ...filters, [field]: isNumeric ? Number(rawValue) : rawValue };
  if (field === "year") {
    next.fromMonth = 1;
    next.toMonth = getMonthOptions(next.year, now).length;
  }
  return next;
};

export const validateFilters = (filters) =>
  filters.fromMonth > filters.toMonth ? "From month cannot be after To month." : "";

// An empty selection means "everything", for both the API and the client-side filter.
const matchesSelection = (selected, value) => selected.length === 0 || selected.includes(value);

export const hasSelection = (filters) =>
  filters.categories.length > 0 ||
  filters.subTypes.length > 0 ||
  (filters.companyIds?.length ?? 0) > 0 ||
  (filters.sellerIds?.length ?? 0) > 0 ||
  (filters.warehouseIds?.length ?? 0) > 0;

export const filterRows = (rows, categories, subTypes) =>
  rows.filter((row) => matchesSelection(categories, row.category) && matchesSelection(subTypes, row.subType));

export const getCategoryOptions = (rows) => {
  const extras = [...new Set(rows.map((row) => row.category))].filter(
    (category) => category && !KNOWN_CATEGORIES.has(category)
  );
  return [...KNOWN_CATEGORIES, ...extras].map((value) => ({ value, label: getCategoryLabel(value) }));
};

export const getTypeOptions = (rows, categories) => {
  const types = rows.filter((row) => matchesSelection(categories, row.category)).map((row) => row.subType);
  return [...new Set(types)].filter(Boolean).sort((a, b) => a.localeCompare(b));
};

// Drops selected types that no longer belong to the selected categories.
export const pruneSubTypes = (filters, optionRows) => {
  const allowed = new Set(getTypeOptions(optionRows, filters.categories));
  return { ...filters, subTypes: filters.subTypes.filter((type) => allowed.has(type)) };
};

// Wallet recharge gross is the seller's own top-up (spent elsewhere in the report), so only its net counts in totals.
const isWalletRechargeRow = (row) =>
  row.category === "Tasks" && String(row.subType ?? "").trim().toLowerCase() === "wallet recharge";

export const sumTotals = (rows) => {
  const totals = rows.reduce(
    (acc, row) => ({
      gross: acc.gross + (isWalletRechargeRow(row) ? 0 : toAmount(row.grossAmount)),
      cost: acc.cost + toAmount(row.costAmount),
      adjustment: acc.adjustment + toAmount(row.adjustmentAmount),
      net: acc.net + toAmount(row.netAmount),
    }),
    { gross: 0, cost: 0, adjustment: 0, net: 0 }
  );
  return {
    gross: round2(totals.gross),
    cost: round2(totals.cost),
    adjustment: round2(totals.adjustment),
    net: round2(totals.net),
  };
};

// Keeps the API order inside a month but makes each category contiguous.
const groupByCategory = (rows) => {
  const byCategory = new Map();
  rows.forEach((row) => {
    if (!byCategory.has(row.category)) byCategory.set(row.category, []);
    byCategory.get(row.category).push(row);
  });
  return [...byCategory.values()].flat();
};

export const groupRowsByMonth = (rows) => {
  const byMonth = new Map();
  rows.forEach((row) => {
    const key = monthKey(row);
    if (!byMonth.has(key)) byMonth.set(key, { key, year: row.year, month: row.month, rows: [] });
    byMonth.get(key).rows.push(row);
  });
  return [...byMonth.values()]
    .sort((a, b) => b.key.localeCompare(a.key))
    .map((group) => ({
      ...group,
      rows: groupByCategory(group.rows),
      totals: sumTotals(group.rows),
      status: getMonthStatus(group.rows),
    }));
};

// Only ended months can be refreshed; the current month is always calculated live.
export const getLastRefreshableMonth = (year, now = new Date()) => {
  const currentYear = now.getUTCFullYear();
  if (Number(year) < currentYear) return 12;
  if (Number(year) > currentYear) return 0;
  return now.getUTCMonth();
};

export const getRefreshMonthOptions = (year, now = new Date()) => {
  const lastRefreshable = getLastRefreshableMonth(year, now);
  return getMonthOptions(year, now).map((option) => {
    const disabled = option.value > lastRefreshable;
    return { ...option, label: disabled ? `${option.label} (live)` : option.label, disabled };
  });
};

export const getDefaultRefreshRange = (now = new Date()) => {
  const { year, fromMonth, toMonth } = getDefaultFilters(now);
  return { year, fromMonth, toMonth };
};

export const changeRefreshYear = (year, now = new Date()) => {
  const month = Math.max(getLastRefreshableMonth(year, now), 1);
  return { year: Number(year), fromMonth: month, toMonth: month };
};

export const validateRefreshRange = (range, now = new Date()) => {
  if (range.fromMonth > range.toMonth) return "From month cannot be after To month.";
  if (range.toMonth > getLastRefreshableMonth(range.year, now)) {
    return "The current month is calculated live and cannot be refreshed.";
  }
  return "";
};

export const getAdjustmentLines = (lines) => lines.filter((line) => toAmount(line.adjustment) !== 0);

const escapeCsvCell = (value) => {
  if (typeof value === "number") return value.toFixed(2);
  let text = String(value ?? "");
  // Neutralise spreadsheet formulas coming from free-text values.
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return /[",\n\r']/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
};

export const buildCsv = (groups, totals) => {
  const header = ["Month", "Category", "Type", "Gross amount", "Net amount", "Status"];
  const body = groups.flatMap((group) =>
    group.rows.map((row) => [
      monthLabel(group.year, group.month),
      getCategoryLabel(row.category),
      row.subType,
      toAmount(row.grossAmount),
      toAmount(row.netAmount),
      getStatusInfo(row).label,
    ])
  );
  const grandTotal = ["Grand total", "", "", totals.gross, totals.net, ""];
  return [header, ...body, grandTotal].map((cells) => cells.map(escapeCsvCell).join(",")).join("\n");
};

export const buildCsvFilename = (filters) =>
  `pnl-summary-${filters.year}-${padMonth(filters.fromMonth)}-to-${padMonth(filters.toMonth)}.csv`;

export const downloadCsv = (filename, content) => {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

const LAST_REFRESHED_KEY = "pnlSummaryLastRefreshed";

// The API returns no refresh timestamp, so the last successful refresh is remembered per browser.
export const readLastRefreshed = () => {
  try {
    return localStorage.getItem(LAST_REFRESHED_KEY) || "";
  } catch {
    return "";
  }
};

export const saveLastRefreshed = (isoString) => {
  try {
    localStorage.setItem(LAST_REFRESHED_KEY, isoString);
  } catch {
    // Storage can be unavailable (private mode); the label just stays as it was.
  }
};

export const formatLastRefreshed = (isoString) =>
  isoString ? moment(isoString).format("DD MMM YYYY, hh:mm A") : "Not refreshed yet";
